import { API_CACHE_KEYS } from "api/cacheKeys";
import { CachePolicy } from "api/types";
import { useCustomQuery } from "api/useCustomQuery";
import { JANZU_URL } from "config/config";
import { Inspection } from "models";
import { ListResponse } from "types/api";

// URL builders
const BASE_URL = `${JANZU_URL}device-api/inspections`;

const buildInspectionDetailUrl = (id: string): string => `${BASE_URL}/${id}`;

// Manual fetch function for imperative use
export const fetchInspectionDetail = async (id: string): Promise<Inspection> => {
  const url = buildInspectionDetailUrl(id);
  const response = await fetch(url, {
    headers: {
      "Cache-Control": CachePolicy.NO_CACHE,
      Pragma: CachePolicy.NO_CACHE,
    },
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch inspection: ${response.statusText}`);
  }
  return response.json();
};

// Query hooks
type UseGetInspectionsParams = {
  url: string;
  enabled?: boolean;
};

const useGetInspections = ({ url, enabled = true }: UseGetInspectionsParams) =>
  useCustomQuery<ListResponse<Inspection>>(API_CACHE_KEYS.inspections.list(url), url, {
    enabled,
    cachePolicy: CachePolicy.NO_CACHE,
  });

type UseGetInspectionDetailParams = {
  id?: string;
  enabled?: boolean;
};

const useGetInspectionDetail = ({ id, enabled = true }: UseGetInspectionDetailParams) => {
  const url = id ? buildInspectionDetailUrl(id) : "";

  return useCustomQuery<Inspection>(API_CACHE_KEYS.inspections.detail(id || ""), url, {
    enabled: enabled && !!id,
    cachePolicy: CachePolicy.NO_CACHE,
  });
};

// Main hook
export type UseInspectionsParams = {
  listUrl?: string;
  detailId?: string;
  listEnabled?: boolean;
  detailEnabled?: boolean;
};

export const useInspections = ({
  listUrl,
  detailId,
  listEnabled = true,
  detailEnabled = true,
}: UseInspectionsParams = {}) => {
  const inspections = useGetInspections({ url: listUrl || "", enabled: listEnabled && !!listUrl });
  const inspectionDetail = useGetInspectionDetail({ id: detailId, enabled: detailEnabled });

  return {
    // List operation
    inspections: inspections.data,
    isLoadingInspections: inspections.isLoading,
    isFetchingInspections: inspections.isFetching,
    inspectionsError: inspections.error,
    refetchInspections: inspections.refetch,
    inspectionsResponse: inspections.response,

    // Detail operation
    inspection: inspectionDetail.data,
    isLoadingInspection: inspectionDetail.isLoading,
    inspectionError: inspectionDetail.error,
    refetchInspection: inspectionDetail.refetch,
    inspectionResponse: inspectionDetail.response,

    // Manual fetch function
    fetchInspectionDetail,
  };
};
